import { useCallback, useMemo, useState, type ComponentType } from "react";
import { CircleDashed, Globe, Instagram, MapPin } from "lucide-react";
import { toggleAxis } from "@/app/routes/dashboard/components/WallBar";
import type { TrendType } from "@/types/trend.type";
import type { TrendAxisKey, TrendPlatform, TrendSort } from "./api/useTrends";
import TikTokIcon from "./components/TikTokIcon";
import { PLATFORM_LABEL, TYPE_LABEL, TYPE_META, TYPE_OPTIONS } from "./components/trendMeta";

/**
 * THE TRENDS FILTERS, ONCE. One state and one description of the groups, drawn
 * three ways: the bar's rails and facets at rest, the glass panel behind the
 * scrolled header's FILTERS facet (chips), and the phone's filter sheet (rows).
 * All three read and write the same state, so a change in one is a change in
 * all of them, and none of them can disagree about what a group holds.
 *
 * Search is not a filter here: it has its own field and its own submit, and it
 * changes what Sort can do rather than being one of its answers.
 */

export type FilterIcon = ComponentType<{ className?: string }>;

/** Region, as the viewer picks it. Underneath it is still the region and
 *  language PAIR a discovery run plans (TREND_AXES), because sending half a
 *  pair asks for a combination no run produces. Only the words changed. */
export const REGION_OPTIONS: readonly {
  value: Exclude<TrendAxisKey, "all">;
  label: string;
  icon: FilterIcon;
}[] = [
  { value: "mena-ar", label: "MENA", icon: MapPin },
  { value: "global-en", label: "Global", icon: Globe },
];

/** The clearing row of the Region facet. */
export const ANY_REGION = { label: "Any region", icon: CircleDashed as FilterIcon };

export const SORT_OPTIONS: readonly { value: TrendSort; label: string; face: string }[] = [
  { value: "newest", label: "Newest", face: "Newest" },
  { value: "views", label: "Most viewed", face: "Views" },
  { value: "likes", label: "Most liked", face: "Likes" },
];

/** What the Sort facet's face prints: "SORT VIEWS" reads whole beside its
 *  caption, and the bar has no width for "SORT MOST VIEWED" at 1280. The
 *  popover rows keep the full labels. */
export function sortFaceLabel(sort: TrendSort): string {
  return SORT_OPTIONS.find((option) => option.value === sort)?.face ?? "Newest";
}

/** What Sort reads while a search is active: the vector path orders by
 *  relevance and ignores `sort`, so that is the only order there is. */
export const SEARCH_ORDER_LABEL = "Best match";

export const SOURCE_OPTIONS: readonly { value: TrendPlatform; label: string; icon: FilterIcon }[] = [
  { value: "tiktok", label: PLATFORM_LABEL.tiktok, icon: TikTokIcon },
  { value: "instagram", label: PLATFORM_LABEL.instagram, icon: Instagram },
];

/**
 * A click on a Kind or Source option, on THIS wall.
 *
 * The shared toggle adds or removes the one value. Here, a set that would end up
 * holding every option of its axis is cleared instead: every kind (or both
 * sources) asks for exactly what no pick asks for, and left as a full set it
 * would still read as a filter, keep the chips lit, print in the readout and fan
 * out one request per value for an answer the single unfiltered request already
 * gives. Cleared, the chips, the readout and the requests all return to "any".
 * Kept here, not in the shared toggle, because References reads a full set its
 * own way.
 */
function toggleTrendAxis<T>(set: readonly T[], value: T, all: readonly T[]): T[] {
  const next = toggleAxis(set, value);
  return all.every((option) => next.includes(option)) ? [] : next;
}

const ALL_SOURCES: readonly TrendPlatform[] = SOURCE_OPTIONS.map((option) => option.value);

export interface TrendFilterState {
  types: TrendType[];
  platforms: TrendPlatform[];
  region: TrendAxisKey;
  sort: TrendSort;
}

export interface TrendFilterActions {
  toggleType: (type: TrendType) => void;
  clearTypes: () => void;
  toggleSource: (platform: TrendPlatform) => void;
  clearSources: () => void;
  /** "all" clears the axis. */
  pickRegion: (region: TrendAxisKey) => void;
  pickSort: (sort: TrendSort) => void;
  /** Every filter back to rest, Sort to Newest. The search is left alone. */
  clearAll: () => void;
}

/**
 * The filter state and every way to change it. `onChange` runs after any
 * change, which is where the page goes back to its first page: a new question
 * starts from the top of its answer.
 */
export function useTrendFilters(onChange: () => void): {
  state: TrendFilterState;
  actions: TrendFilterActions;
} {
  /* Each of Kind and Source holds a SET. Empty is the unfiltered state, which
     is why there is no ALL option on either. */
  const [types, setTypes] = useState<TrendType[]>([]);
  const [platforms, setPlatforms] = useState<TrendPlatform[]>([]);
  const [region, setRegion] = useState<TrendAxisKey>("all");
  const [sort, setSort] = useState<TrendSort>("newest");

  const changed = useCallback(() => onChange(), [onChange]);
  const state = useMemo(() => ({ types, platforms, region, sort }), [types, platforms, region, sort]);
  const actions = useMemo<TrendFilterActions>(
    () => ({
      toggleType: (type) => {
        setTypes((set) => toggleTrendAxis(set, type, TYPE_OPTIONS));
        changed();
      },
      clearTypes: () => {
        setTypes([]);
        changed();
      },
      toggleSource: (platform) => {
        setPlatforms((set) => toggleTrendAxis(set, platform, ALL_SOURCES));
        changed();
      },
      clearSources: () => {
        setPlatforms([]);
        changed();
      },
      pickRegion: (next) => {
        setRegion(next);
        changed();
      },
      pickSort: (next) => {
        setSort(next);
        changed();
      },
      clearAll: () => {
        setTypes([]);
        setPlatforms([]);
        setRegion("all");
        setSort("newest");
        changed();
      },
    }),
    [changed],
  );
  return { state, actions };
}

/** Anything narrowed or re-ordered. What CLEAR ALL clears and the phone's
 *  Filters dot marks. */
export function anyFilterSet(state: TrendFilterState): boolean {
  return (
    state.types.length > 0 ||
    state.platforms.length > 0 ||
    state.region !== "all" ||
    state.sort !== "newest"
  );
}

export function regionWord(region: TrendAxisKey): string | null {
  return REGION_OPTIONS.find((option) => option.value === region)?.label ?? null;
}

export function sortWord(sort: TrendSort): string {
  return SORT_OPTIONS.find((option) => option.value === sort)?.label ?? "Newest";
}

/** The FILTERS facet's value, one word per active filter in group order, with
 *  Sort only when it is not the default. Empty when nothing is set. */
export function trendFilterSummary(state: TrendFilterState): string[] {
  const region = regionWord(state.region);
  return [
    ...state.types.map((type) => TYPE_LABEL[type]),
    ...state.platforms.map((platform) => PLATFORM_LABEL[platform] ?? platform),
    ...(region ? [region] : []),
    ...(state.sort !== "newest" ? [sortWord(state.sort)] : []),
  ];
}

/**
 * One group of filters, drawn as chips on a desktop and as rows on a phone.
 *
 *  multi   any number, each click adds or removes one (Kind, Source)
 *  single  at most one, and clicking the picked one clears it (Region)
 *  one     exactly one, always (Sort)
 */
export interface TrendFilterGroup {
  key: "kind" | "source" | "region" | "sort";
  caption: string;
  mode: "multi" | "single" | "one";
  /** Sort while searching: the vector path ignores it. */
  disabled: boolean;
  options: { value: string; label: string; icon?: FilterIcon; selected: boolean }[];
  pick: (value: string) => void;
}

export function trendFilterGroups(
  state: TrendFilterState,
  actions: TrendFilterActions,
  searching: boolean,
): TrendFilterGroup[] {
  return [
    {
      key: "kind",
      caption: "Kind",
      mode: "multi",
      disabled: false,
      options: TYPE_OPTIONS.map((type) => ({
        value: type,
        label: TYPE_LABEL[type],
        icon: TYPE_META[type].icon,
        selected: state.types.includes(type),
      })),
      pick: (value) => actions.toggleType(value as TrendType),
    },
    {
      key: "source",
      caption: "Source",
      mode: "multi",
      disabled: false,
      options: SOURCE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        icon: option.icon,
        selected: state.platforms.includes(option.value),
      })),
      pick: (value) => actions.toggleSource(value as TrendPlatform),
    },
    {
      key: "region",
      caption: "Region",
      mode: "single",
      disabled: false,
      options: REGION_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        icon: option.icon,
        selected: state.region === option.value,
      })),
      pick: (value) =>
        actions.pickRegion(state.region === value ? "all" : (value as TrendAxisKey)),
    },
    {
      key: "sort",
      caption: "Sort",
      mode: "one",
      disabled: searching,
      options: SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        selected: state.sort === option.value,
      })),
      pick: (value) => actions.pickSort(value as TrendSort),
    },
  ];
}
